"use client"

import { ADMIN_API_CONFIG } from "@/constants/api-configs"
import { t } from "@/languages/i18n"
import { DefaultImage } from "@/lib/external-components"
import { MenuApiResponse, MenuItem } from "@/types/types"
import { DownOutlined } from "@ant-design/icons"
import { ComponentConfig } from "@measured/puck"
import { message } from "antd"
import { useEffect, useState } from "react"
import menuService from "../../../../../menu/service/menuService"
import { CommonStylesProps } from "../../../lib/commonCSSProps"
import { generateResponsiveCSS } from "../../../lib/helper"

export interface NavigationProps extends CommonStylesProps {
  url: string
  menu: string
  slogan?: string
  logoStyles?: {
    width: string
    height: string
    padding: string
    margin: string
    objectFit: "cover" | "contain" | "fill" | "none" | "scale-down"
  }
  sloganStyles?: {
    fontSize: string
    fontWeight: string
    color: string
    margin: string
    padding: string
  }
  menuStyles?: {
    itemPadding?: string
    itemMargin?: string
    itemColor?: string
    itemHoverColor?: string
    itemFontSize?: string
    itemFontWeight?: string

    dropdownBackgroundColor?: string
    dropdownBorderRadius?: string
    dropdownShadow?: string

    submenuItemColor?: string
    submenuItemHoverColor?: string
    submenuItemHoverBackground?: string
    submenuItemPadding?: string
    submenuItemFontSize?: string
    submenuItemFontWeight?: string
  }
  menuContainerStyles?: {
    display?: "flex" | "block" | "inline-flex"
    flexDirection?: "row" | "column"
    justifyContent?: "start" | "end" | "center" | "between" | "around" | "evenly"
    alignItems?: "start" | "end" | "center" | "baseline" | "stretch"
    gap?: string
    padding?: string
    margin?: string
    width?: string
  }
  menuLinkStyles?: {
    fontFamily?: string
    fontSize?: string
    fontWeight?: string
    fontStyle?: string
    textTransform?: "uppercase" | "lowercase" | "capitalize" | "none"
    color?: string
    hoverColor?: string
    textDecoration?: string
    hoverTextDecoration?: string
    letterSpacing?: string
    lineHeight?: string
    padding?: string
    margin?: string
    borderBottom?: string
    hoverBorderBottom?: string
    transition?: string
  }
  type: "fluid" | "custom" | "boxed"
}

interface MenuItemWithSubItems extends MenuItem {
  subItems?: MenuItem[]
}

export const RenderConfig: ComponentConfig<NavigationProps> = {
  render: ({
    url,
    menu,
    slogan,
    logoStyles,
    sloganStyles = {},
    menuStyles = {},
    className,
    type,
    styles,
    responsiveType,
    menuContainerStyles = {},
    menuLinkStyles = {},
  }) => {
    const id = `navigation-${Math.random().toString(36).substr(2, 9)}`
    const [organizedMenuItems, setOrganizedMenuItems] = useState<MenuItemWithSubItems[]>([])
    const [isOn, setIsOn] = useState(false)

    const onColor = "bg-white/50"
    const offColor = "bg-white/50"
    const handleToggle = () => {
      setIsOn(prev => !prev)
    }
    const organizeMenuItems = (items: MenuItem[]): MenuItemWithSubItems[] => {
      const sortedItems = [...items].sort((a, b) => a.order - b.order)

      const parentItems = sortedItems.filter(item => item.parent === 0)

      let currentParentIndex = -1
      const childrenGroups: { [parentId: number]: MenuItem[] } = {}

      sortedItems.forEach(item => {
        if (item.parent === 0) {
          currentParentIndex = item.id
        } else if (item.parent === 1 && currentParentIndex !== -1) {
          if (!childrenGroups[currentParentIndex]) {
            childrenGroups[currentParentIndex] = []
          }
          childrenGroups[currentParentIndex].push(item)
        }
      })

      return parentItems.map(parent => ({
        ...parent,
        subItems: childrenGroups[parent.id] || [],
      }))
    }

    const fetchMenuItems = async () => {
      if (!menu) return

      try {
        const response = await menuService.fetchAllMenuPreference()
        const data = response.data

        if (data.length > 0) {
          const selectedMenuPreference = data.find((item: MenuApiResponse) => item.attributes.name === menu)

          if (selectedMenuPreference) {
            const organized = organizeMenuItems(selectedMenuPreference.attributes.items || [])
            setOrganizedMenuItems(organized)
          } else {
            setOrganizedMenuItems([])
          }
        }
      } catch (error) {
        message.error(t("Error fetching menu preferences. Please try again."))
      }
    }

    useEffect(() => {
      fetchMenuItems()
    }, [menu])
    const responsiveCSS = generateResponsiveCSS(id, styles || {}, responsiveType)

    return (
      <>
        {responsiveCSS}
        <div id={id}>
          <div className={`w-full flex flex-row justify-between items-center gap-[152.17px] ${className}`}>
            <img
              src={url ? `${ADMIN_API_CONFIG.API_URL}${url}` : DefaultImage}
              style={{
                width: logoStyles?.width || "100%",
                height: logoStyles?.height || "73px",
                objectFit: logoStyles?.objectFit || "cover",
                padding: logoStyles?.padding || "0px",
                margin: logoStyles?.margin || "0px",
              }}
              className="cursor-pointer"
              alt="Logo"
            />

            <div className="flex">
              <ul
                className={`
                    flex 
                    ${menuContainerStyles.flexDirection === "column" ? "flex-col" : "flex-row"}
                    ${
                      menuContainerStyles.justifyContent
                        ? `justify-${menuContainerStyles.justifyContent}`
                        : "justify-end"
                    }
                    ${menuContainerStyles.alignItems ? `items-${menuContainerStyles.alignItems}` : "items-center"}
                  `}
                style={{
                  display: menuContainerStyles.display || "flex",
                  gap: menuContainerStyles.gap || "0.75rem",
                  padding: menuContainerStyles.padding || "0",
                  margin: menuContainerStyles.margin || "0",
                  width: menuContainerStyles.width || "auto",
                }}
              >
                {organizedMenuItems.map(item => (
                  <li
                    key={item.id}
                    className="relative cursor-pointer duration-200 transition-colors group pb-2"
                    style={{
                      padding: menuStyles.itemPadding || "0.5rem",
                      color: menuStyles.itemColor || "inherit",
                      fontSize: menuStyles.itemFontSize || "inherit",
                      fontWeight: menuStyles.itemFontWeight || "inherit",
                    }}
                  >
                    <div className="flex flex-row gap-2 items-center justify-center">
                      <a
                        href={item.slug ? `/${item.slug}` : "#"}
                        className="font-semibold uppercase"
                        style={{
                          fontFamily: menuLinkStyles.fontFamily || "inherit",
                          fontSize: menuLinkStyles.fontSize || "inherit",
                          fontWeight: menuLinkStyles.fontWeight || "600",
                          fontStyle: menuLinkStyles.fontStyle || "normal",
                          textTransform: menuLinkStyles.textTransform || "uppercase",
                          color: menuLinkStyles.color || "inherit",
                          textDecoration: menuLinkStyles.textDecoration || "none",
                          letterSpacing: menuLinkStyles.letterSpacing || "normal",
                          lineHeight: menuLinkStyles.lineHeight || "normal",
                          padding: menuLinkStyles.padding || "0",
                          margin: menuLinkStyles.margin || "0",
                          borderBottom: menuLinkStyles.borderBottom || "none",
                          transition: menuLinkStyles.transition || "all 0.3s ease",
                        }}
                      >
                        {item.name}
                      </a>
                      {item.subItems?.length !== 0 ? <DownOutlined style={{ fontSize: "8px" }} /> : ""}
                    </div>

                    {item.subItems && item.subItems.length > 0 && (
                      <ul className="absolute hidden border group-hover:block bg-white shadow-lg rounded-md mt-2 min-w-[200px] z-50">
                        {item.subItems.map(subItem => (
                          <a
                            key={subItem.id}
                            href={subItem.slug ? `/${subItem.slug}` : "#"}
                            className="block px-4 py-2 text-gray-700 duration-200 transition-colors hover:text-teal-600 text-nowrap"
                          >
                            {subItem.name}
                          </a>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-row gap-3 items-center">
              <div>
                <input
                  checked={isOn}
                  onChange={handleToggle}
                  className="hidden"
                  id="react-switch-new"
                  type="checkbox"
                />
                <label
                  className={`flex items-center justify-between w-[100px] h-8 rounded-full cursor-pointer transition-colors duration-200 ${
                    isOn ? onColor : offColor
                  }`}
                  htmlFor="react-switch-new"
                >
                  <span
                    className={`w-12 h-7 bg-white rounded-full transform flex items-center justify-center text-black text-sm font-bold transition-transform duration-200 select-none text-center ${
                      isOn ? "translate-x-12" : "translate-x-[0.2rem]"
                    }`}
                  >
                    {isOn ? "EN" : "VI"}
                  </span>
                </label>
              </div>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M18.031 16.617L22.314 20.899L20.899 22.314L16.617 18.031C15.0237 19.3082 13.042 20.0029 11 20C6.032 20 2 15.968 2 11C2 6.032 6.032 2 11 2C15.968 2 20 6.032 20 11C20.0029 13.042 19.3082 15.0237 18.031 16.617ZM16.025 15.875C17.2941 14.5699 18.0029 12.8204 18 11C18 7.132 14.867 4 11 4C7.132 4 4 7.132 4 11C4 14.867 7.132 18 11 18C12.8204 18.0029 14.5699 17.2941 15.875 16.025L16.025 15.875Z"
                  fill="white"
                />
              </svg>
            </div>
          </div>
        </div>
      </>
    )
  },
}
